import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BookOpen, Search, X, Copy, Check, Loader2, Monitor, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERSIONS = ['KJV', 'NIV', 'AMP', 'MSG', 'ASV', 'WEB'];

/**
 * ScriptureLookupBar — Floating Cmd+K style scripture lookup.
 * Type "John 3:16" or "Psalm 23:1-6 NIV" to instantly fetch verse text.
 */
export default function ScriptureLookupBar({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [version, setVersion] = useState('KJV');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setResult(null);
      setError('');
      setQuery('');
    }
  }, [isOpen]);

  // Keyboard shortcut: Escape to close
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const lookup = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const r = await axios.get(`${API}/propresenter/scripture/lookup?ref=${encodeURIComponent(query)}&version=${version}`);
      setResult(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not find that reference';
      setError(detail);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') lookup();
  };

  const copyText = () => {
    if (!result) return;
    const text = `${result.text}\n— ${result.reference} (${result.version})`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh]" onClick={onClose} data-testid="scripture-lookup-overlay">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl mx-4 animate-in fade-in slide-in-from-top-4 duration-200" onClick={e => e.stopPropagation()} data-testid="scripture-lookup-panel">
        {/* Search Input */}
        <div className="bg-[#12182a] rounded-2xl border border-white/15 shadow-2xl overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
            <BookOpen className="w-5 h-5 text-[#B8860B] flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a reference… John 3:16, Psalm 23:1-6, Rom 8:28"
              className="flex-1 bg-transparent text-white text-base border-none focus:ring-0 p-0 placeholder:text-white/25"
              data-testid="scripture-lookup-input"
            />
            {/* Version selector */}
            <div className="relative">
              <button
                onClick={() => setShowVersions(!showVersions)}
                className="px-2.5 py-1 bg-white/8 hover:bg-white/12 text-white/60 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
                data-testid="lookup-version-btn"
              >
                {version} <ChevronDown className="w-3 h-3" />
              </button>
              {showVersions && (
                <div className="absolute right-0 top-full mt-1 bg-[#1a2235] border border-white/10 rounded-lg shadow-xl py-1 z-10 min-w-[120px]">
                  {VERSIONS.map(v => (
                    <button
                      key={v}
                      onClick={() => { setVersion(v); setShowVersions(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${v === version ? 'text-[#B8860B] bg-[#B8860B]/10' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}
                      data-testid={`lookup-version-${v}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={lookup}
              disabled={loading || !query.trim()}
              className="px-4 py-1.5 bg-[#B8860B] hover:bg-[#D19C11] text-white rounded-lg text-sm font-medium disabled:opacity-30 transition-colors flex items-center gap-1.5"
              data-testid="lookup-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Lookup
            </button>
            <button onClick={onClose} className="text-white/30 hover:text-white/60 transition-colors" data-testid="lookup-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Results Area */}
          {(result || error || loading) && (
            <div className="px-5 py-4 max-h-[50vh] overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center py-8 gap-2">
                  <Loader2 className="w-5 h-5 text-[#B8860B] animate-spin" />
                  <span className="text-white/40 text-sm">Looking up scripture...</span>
                </div>
              )}

              {error && !loading && (
                <div className="text-center py-6">
                  <p className="text-red-400/70 text-sm mb-1">{error}</p>
                  <p className="text-white/20 text-xs">Try formats like: John 3:16, Psalm 23:1-6, Gen 1:1</p>
                </div>
              )}

              {result && !loading && (
                <div data-testid="lookup-result">
                  {/* Reference header */}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-white text-lg font-semibold" data-testid="lookup-result-ref">{result.reference}</h3>
                      <p className="text-[#B8860B]/70 text-xs">{result.version_name} ({result.version})</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={copyText}
                        className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/50 hover:text-white rounded-lg text-xs flex items-center gap-1.5 transition-colors"
                        data-testid="lookup-copy"
                      >
                        {copied ? <><Check className="w-3.5 h-3.5 text-green-400" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                      </button>
                    </div>
                  </div>

                  {/* Verse text */}
                  <div className="space-y-2">
                    {result.verses.map(v => (
                      <div key={v.num} className="flex gap-2.5" data-testid={`lookup-verse-${v.num}`}>
                        <span className="text-[#B8860B] text-xs font-bold w-5 text-right flex-shrink-0 pt-1">{v.num}</span>
                        <p className="text-white/85 text-sm leading-relaxed" style={{ fontFamily: "'Georgia', serif" }}>
                          {v.text}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Quick actions */}
                  <div className="mt-4 pt-3 border-t border-white/8 flex items-center gap-2">
                    <p className="text-white/15 text-[10px] flex-1">Press Enter to search · Esc to close</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hint bar when empty */}
          {!result && !error && !loading && (
            <div className="px-5 py-3 border-t border-white/5">
              <div className="flex flex-wrap gap-2">
                {['John 3:16', 'Psalm 23:1-6', 'Rom 8:28', 'Gen 1:1', 'Phil 4:13', 'Jer 29:11'].map(ex => (
                  <button
                    key={ex}
                    onClick={() => { setQuery(ex); }}
                    className="px-2.5 py-1 bg-white/5 hover:bg-white/10 text-white/30 hover:text-white/60 rounded-lg text-[11px] transition-colors"
                    data-testid={`lookup-example-${ex.replace(/\s/g, '-').replace(/:/g, '-')}`}
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
